/**
 * Runtime configuration (task 20.3, R27.4).
 *
 * The SPA is a static bundle on S3 behind CloudFront, so it must NOT bake
 * environment-specific URLs/ids into the JavaScript. Instead it reads them at
 * runtime from one of two sources, in order:
 *
 *   1. `window.__RUFUS_CONFIG__` — an object injected by an inline script (used
 *      in tests and when a host wants to inject config without a fetch), OR
 *   2. `/config.json` — a JSON document written into the site bucket by the
 *      FrontendStack `BucketDeployment` at deploy time (the production path).
 *
 * Either way the same built artifact deploys to any stage; only the config
 * document differs. The values are the outputs the ComputeStack exposes:
 * the REST API base URL, the WebSocket (`wss://`) URL, and the Cognito
 * user-pool id + app-client id (task 20.1).
 */

/** The social identity providers the Hosted UI may offer (R24.4). */
export type SocialProvider = "Google" | "Amazon";

export interface RufusRuntimeConfig {
  /** REST API base URL (ComputeStack `apiUrl`). */
  readonly apiUrl: string;
  /** WebSocket push-channel URL, `wss://…` (ComputeStack `wsApiUrl`). */
  readonly wsUrl: string;
  /** Cognito user-pool id (ComputeStack `userPool`). */
  readonly userPoolId: string;
  /** Cognito app-client id for the browser SPA (ComputeStack `userPoolClient`). */
  readonly userPoolClientId: string;
  /**
   * Cognito Hosted UI base URL, e.g. `https://auth.rufus.jovial.ninja`
   * (ComputeStack `authBaseUrl`, R24.4). The SPA redirects here for the OAuth
   * authorization-code (PKCE) flow — sign-in, first-login password set, TOTP
   * enrollment, and social federation all happen in the Hosted UI.
   */
  readonly authDomain: string;
  /**
   * Social IdPs enabled on the Hosted UI (R24.4), so the login screen renders
   * only the buttons that are actually wired. Empty ⇒ native email/password
   * only. Omitted in a config document is treated as empty.
   */
  readonly socialProviders?: readonly SocialProvider[];
  /**
   * Build provenance (git SHA + build time) the FrontendStack stamps into
   * `config.json` so the running SPA knows exactly which build it is — a debug
   * aid for correlating a UI report to deployed code. Optional: an older config
   * document without it is still valid.
   */
  readonly buildInfo?: { readonly gitSha: string; readonly builtAt: string };
}

declare global {
  interface Window {
    __RUFUS_CONFIG__?: RufusRuntimeConfig;
  }
}

function isSocialProviderList(value: unknown): value is readonly SocialProvider[] {
  return (
    Array.isArray(value) &&
    value.every((p) => p === "Google" || p === "Amazon")
  );
}

function isConfig(value: unknown): value is RufusRuntimeConfig {
  if (typeof value !== "object" || value === null) return false;
  const c = value as Record<string, unknown>;
  return (
    typeof c.apiUrl === "string" &&
    typeof c.wsUrl === "string" &&
    typeof c.userPoolId === "string" &&
    typeof c.userPoolClientId === "string" &&
    typeof c.authDomain === "string" &&
    // socialProviders is optional; when present it must be a list of known ids.
    (c.socialProviders === undefined || isSocialProviderList(c.socialProviders))
  );
}

let cached: RufusRuntimeConfig | null = null;

/**
 * Resolve the runtime config once, preferring an injected
 * `window.__RUFUS_CONFIG__` and otherwise fetching `/config.json`. The result
 * is memoized so screens can call it freely. Throws if neither source yields a
 * well-formed config — a misconfigured deploy should fail loud, not silently
 * point the SPA at nothing.
 */
export async function loadConfig(
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<RufusRuntimeConfig> {
  if (cached) return cached;

  const injected = globalThis.window?.__RUFUS_CONFIG__;
  if (isConfig(injected)) {
    cached = injected;
    return cached;
  }

  const res = await fetchImpl("/config.json", { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`Failed to load runtime config: HTTP ${res.status}`);
  }
  const body: unknown = await res.json();
  if (!isConfig(body)) {
    throw new Error("Runtime config is missing required fields.");
  }
  cached = body;
  return cached;
}

/** Test seam: clear the memoized config. */
export function resetConfigForTests(): void {
  cached = null;
}
